import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { UserPlusIcon, PencilIcon, TrashIcon, KeyIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import moment from 'moment';

import API_URL from '../config/api';

const Users = () => {
  const { user: currentUser, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'employee',
    phone: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    // Validate password for new users
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters!');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('User created successfully!');
        setShowAddModal(false);
        resetForm();
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to create user');
      }
    } catch (error) {
      toast.error('Error creating user');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        phone: formData.phone
      };
      
      // Only include password if it's being changed
      if (formData.password && formData.password.length >= 6) {
        updateData.password = formData.password;
      }

      const response = await fetch(`${API_URL}/api/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        toast.success('User updated successfully!');
        setShowEditModal(false);
        setEditingUser(null);
        resetForm();
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Error updating user');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users/${userId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      if (response.ok) {
        toast.success('User status updated!');
        fetchUsers();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Error updating status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('User deleted successfully');
        fetchUsers();
      } else {
        toast.error('Failed to delete user');
      }
    } catch (error) {
      toast.error('Error deleting user');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'employee',
      phone: ''
    });
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '', // Don't pre-fill password
      role: user.role,
      phone: user.phone || ''
    });
    setShowEditModal(true);
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'admin':
        return { background: '#f0f9ff', color: '#0369a1' };
      case 'sales':
        return { background: '#f0fdf4', color: '#15803d' };
      default:
        return { background: '#fef3c7', color: '#d97706' };
    }
  };

  if (loading) return <div className="spinner"></div>;

  if (!isAdmin) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <p>You don't have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 className="page-title" style={{ fontSize: 28, fontWeight: 600 }}>User Management</h1>
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary"
          style={{ 
            background: '#2B7FE0', 
            color: 'white', 
            padding: '10px 20px', 
            borderRadius: 6, 
            border: 'none', 
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <UserPlusIcon style={{ width: 20, height: 20 }} />
          Add User
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          {users.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <p>No users found.</p>
            </div>
          ) : (
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>
                      {user.name}
                      {user.id === currentUser?.id && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#718096' }}>(You)</span>
                      )}
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <span style={{ 
                        padding: '4px 8px', 
                        borderRadius: 4, 
                        fontSize: 12, 
                        fontWeight: 500,
                        ...getRoleBadgeStyle(user.role),
                        textTransform: 'uppercase'
                      }}>
                        {user.role}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => toggleUserStatus(user.id, user.is_active)}
                        style={{ 
                          border: 'none', 
                          background: 'none', 
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                        disabled={user.id === currentUser?.id}
                      >
                        {user.is_active ? (
                          <>
                            <CheckCircleIcon style={{ width: 20, height: 20, color: '#48bb78' }} />
                            <span style={{ color: '#48bb78' }}>Active</span>
                          </>
                        ) : (
                          <>
                            <XCircleIcon style={{ width: 20, height: 20, color: '#f56565' }} />
                            <span style={{ color: '#f56565' }}>Inactive</span>
                          </>
                        )}
                      </button>
                    </td>
                    <td>
                      {user.last_login ? moment(user.last_login).fromNow() : 'Never'}
                    </td>
                    <td>{moment(user.created_at).format('MM/DD/YYYY')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button 
                          onClick={() => openEditModal(user)}
                          title="Edit User"
                          style={{ 
                            border: 'none', 
                            background: 'none', 
                            cursor: 'pointer',
                            padding: 4
                          }}
                        >
                          <PencilIcon style={{ width: 18, height: 18, color: '#718096' }} />
                        </button>
                        {user.id !== currentUser?.id && (
                          <button 
                            onClick={() => handleDeleteUser(user.id)}
                            title="Delete User"
                            style={{ 
                              border: 'none', 
                              background: 'none', 
                              cursor: 'pointer',
                              padding: 4
                            }}
                          >
                            <TrashIcon style={{ width: 18, height: 18, color: '#ef4444' }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)} 
             style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} 
               style={{ background: 'white', borderRadius: 8, padding: 24, width: '90%', maxWidth: 500 }}>
            <h2 style={{ marginBottom: 20 }}>Add New User</h2>
            <form onSubmit={handleAddUser}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e2e8f0' }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e2e8f0' }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Initial Password * (min 6 characters)</label>
                <input
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  minLength="6"
                  placeholder="User's initial password"
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e2e8f0' }}
                />
                <p style={{ fontSize: 12, color: '#718096', marginTop: 4 }}>
                  Share this password with the user. They can change it after logging in.
                </p>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e2e8f0' }}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e2e8f0' }}
                >
                  <option value="employee">Employee</option>
                  <option value="sales">Sales</option>
                  <option value="admin">Admin</option>
                </select>
                <p style={{ fontSize: 12, color: '#718096', marginTop: 4 }}>
                  Admin: Full access | Sales: Lead Finder & Pipeline | Employee: Service calls only
                </p>
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowAddModal(false)} 
                        style={{ padding: '10px 20px', borderRadius: 6, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" 
                        style={{ background: '#2B7FE0', color: 'white', padding: '10px 20px', borderRadius: 6, border: 'none', cursor: 'pointer' }}>
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)} 
             style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} 
               style={{ background: 'white', borderRadius: 8, padding: 24, width: '90%', maxWidth: 500 }}>
            <h2 style={{ marginBottom: 20 }}>Edit User</h2>
            <form onSubmit={handleEditUser}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e2e8f0' }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e2e8f0' }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>New Password (leave blank to keep current)</label>
                <input
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Enter new password (optional)"
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e2e8f0' }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e2e8f0' }}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e2e8f0' }}
                  disabled={editingUser?.id === currentUser?.id}
                >
                  <option value="employee">Employee</option>
                  <option value="sales">Sales</option>
                  <option value="admin">Admin</option>
                </select>
                {editingUser?.id === currentUser?.id && (
                  <p style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>
                    You cannot change your own role
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowEditModal(false)} 
                        style={{ padding: '10px 20px', borderRadius: 6, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" 
                        style={{ background: '#2B7FE0', color: 'white', padding: '10px 20px', borderRadius: 6, border: 'none', cursor: 'pointer' }}>
                  Update User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
